import { DateUtil, ModelRow, OrderByInfo, StringUtil } from "@mcleod/core";
import { getDetentionControlBoolean, LMEGeneralSettings } from "@mcleod/general";
import { isSpotOrder } from "@mcleod/dispatch";
import { getTimeDiff, MovementStatus } from "./BrokeragePlanningUtil";
import { RowBrokeragePlanningProfile } from "./models/ModelBrokeragePlanningProfile";
import { PlanningUtil } from "@mcleod/dispatch/src/planning/PlanningUtil";
import { TableColumn } from "@mcleod/components";
import { Columns } from "./BrokerageMovementTableColumns";

export type Filter = (row: ModelRow, profile: RowBrokeragePlanningProfile) => boolean;

export interface SecondaryFilter {
    id: string;
    caption: string;
    filter: (row: ModelRow) => boolean;
}

interface BrokerageMovementTableTypeProps {
    orderColumnBadgeIds: string[];
    revenueColumnFields: string[];
    caption: string;
    tableIdSuffix: string;
    filter: Filter;
    secondaryFilters?: SecondaryFilter[];
    configureColumn?: (column: TableColumn) => void;
    defaultSort?: OrderByInfo[];
    moveStatus?: string;
}

export class BrokerageMovementTableType implements BrokerageMovementTableTypeProps {
    // secondary filters
    static readonly FILTER_LATE = {
        id: "isLate",
        caption: "Appointment Late",
        filter: (row: ModelRow) => {
            return (this.isLateWarning(row, "origin")
                || this.isLateWarning(row, "destination"));
        }
    }

    static readonly FILTER_TRACKING_LATE = {
        id: "isTrackingLate",
        caption: "Tracking Late",
        filter: (row: ModelRow): boolean => {
            return ["A", "C", "P"].includes(row.getString("move_status")) && this.isTrackingLate(row);
        }
    }

    static readonly TRACKING_REQUIRED = {
        id: "isTrackingRequired",
        caption: "Tracking Required",
        filter: (row: ModelRow): boolean => {
            return ["C", "P"].includes(row.getString("move_status")) && this.trackingRequiredAndActiveVendorExist(row);
        }
    }

    static readonly TRACKING_ALERT = {
        id: "isTrackingAlert",
        caption: "Tracking Alert",
        filter: (row: ModelRow): boolean => {
            return ["D", "C", "P"].includes(row.getString("move_status")) && row.getBoolean("tracking_vendor_active", false)
                && row.getBoolean("tracking_alert", false);
        }
    }

    static readonly LOAD_LOCK_ALERT = {
        id: "isLoadLockAlert",
        caption: "Load Lock Alerts",
        filter: (row: ModelRow): boolean => {
            return row.getBoolean("has_load_lock_alert", false);
        }
    }

    static readonly FILTER_SERV_FAILURE = {
        id: "serviceFailure",
        caption: "Service Failure",
        filter: (row: ModelRow) => row.getBoolean("has_service_failures", false)
    }

    static readonly FILTER_APPT_REQUIRED = {
        id: "apptRequired",
        caption: "Appointment Required",
        filter: (row: ModelRow) => row.get("appt_required_count", 0) > 0
            || BrokerageMovementTableType.stopApptFilter(row, "origin", false)
            || BrokerageMovementTableType.stopApptFilter(row, "destination", false)
    }

    static readonly FILTER_APPT_CONFIRMED = {
        id: "apptConfirmed",
        caption: "Appointment Confirmed",
        filter: (row: ModelRow) => row.get("appt_confirmed_count", 0) > 0
            || BrokerageMovementTableType.stopApptFilter(row, "origin", true)
            || BrokerageMovementTableType.stopApptFilter(row, "destination", true)
    }

    static readonly FILTER_CHECK_CALL_APPR = {
        id: "chkCallApproaching",
        caption: "Approaching Check Call",
        filter: (row: ModelRow) => {
            const warningTime = LMEGeneralSettings.getSingleton().getDispatchControl().check_call_warning;
            const call = row.data["next_sched_call"];
            if (warningTime == null) return false;
            if (call == null) return false;
            const callDate = DateUtil.parseDateTime(call);
            callDate.setMinutes(callDate.getMinutes() + warningTime);
            return callDate > new Date();
        }
    }

    static readonly FILTER_CHECK_CALL_MISS = {
        id: "chkCallMissed",
        caption: "Missed Check Call",
        filter: (row: ModelRow) => {
            const call = row.data["next_sched_call"];
            if (call == null) return false;
            return new Date() > DateUtil.parseDateTime(call);
        }
    }

    static readonly FILTER_SPOT_ORDERS = {
        id: "spotOrders",
        caption: "Spot Orders",
        filter: (row: ModelRow) => {
            return isSpotOrder(row);
        }
    }

    static readonly FILTER_COMMITTED_ORDERS = {
        id: "committedOrders",
        caption: "Committed Orders",
        filter: (row: ModelRow) => {
            return !isSpotOrder(row);
        }
    }

    static readonly FILTER_MISS_NEXT_CALL_DATE = {
        id: "missNextCheckCall",
        caption: "Missing Next Check Call",
        filter: (row: ModelRow) => {
            const call = row.data["last_broker_carrier_sched_callin_date"];
            return call === undefined;
        }
    }

    static readonly FILTER_NO_DRIVER_INFO_TODAY = {
        id: "noDriverInfoToday",
        caption: "No Driver Info Today",
        filter: (row: ModelRow) => {
            const noDriverCell = row.data["carrier_driver_cell"];

            let originStopEarly = row.data["origin_early"];
            if (originStopEarly !== undefined) {
                originStopEarly = new Date(originStopEarly.split(" ")[0]).setUTCHours(0, 0, 0, 0)
            }

            let originStopLate = row.data["origin_late"];
            if (originStopLate !== undefined) {
                originStopLate = new Date(originStopLate.split(" ")[0]).setUTCHours(0, 0, 0, 0)
            }

            const today = new Date().setUTCHours(0, 0, 0, 0);
            return (noDriverCell === undefined)
                && ((originStopEarly !== undefined && today === originStopEarly)
                    || (originStopLate !== undefined && today === originStopLate)
                    || (originStopEarly !== undefined && originStopLate !== undefined && today > originStopEarly && today < originStopLate))
        }
    }

    static readonly FILTER_NO_DRIVER_INFO_TOMORROW = {
        id: "noDriverInfoTomorrow",
        caption: "No Driver Info Tomorrow",
        filter: (row: ModelRow) => {
            const noDriverCell = row.data["carrier_driver_cell"];

            let originStopEarly = row.data["origin_early"];
            if (originStopEarly !== undefined) {
                originStopEarly = new Date(originStopEarly.split(" ")[0]).setUTCHours(0, 0, 0, 0)
            }

            let originStopLate = row.data["origin_late"];
            if (originStopLate !== undefined) {
                originStopLate = new Date(originStopLate.split(" ")[0]).setUTCHours(0, 0, 0, 0);
            }

            let tomorrow: number | Date = new Date();
            tomorrow.setDate((new Date()).getDate() + 1);
            tomorrow = tomorrow.setUTCHours(0, 0, 0, 0);
            return (noDriverCell === undefined)
                && ((originStopEarly !== undefined && tomorrow === originStopEarly)
                    || (originStopLate !== undefined && tomorrow === originStopLate)
                    || (originStopEarly !== undefined && originStopLate !== undefined && tomorrow > originStopEarly && tomorrow < originStopLate))
        }
    }

    static readonly FILTER_NO_DRIVER_INFO_TOTAL = {
        id: "noDriverInfoTotal",
        caption: "No Driver Info",
        filter: (row: ModelRow) => {
            const noDriverCell = row.data["carrier_driver_cell"];

            let originStopEarly = row.data["origin_early"];
            if (originStopEarly !== undefined) {
                originStopEarly = new Date(originStopEarly.split(" ")[0]).setUTCHours(0, 0, 0, 0)
            }

            let originStopLate = row.data["origin_late"];
            if (originStopLate !== undefined) {
                originStopLate = new Date(originStopLate.split(" ")[0]).setUTCHours(0, 0, 0, 0)
            }

            const today = new Date().setUTCHours(0, 0, 0, 0);
            let tomorrow: number | Date = new Date();
            tomorrow.setDate((new Date()).getDate() + 1);
            tomorrow = tomorrow.setUTCHours(0, 0, 0, 0)
            return (noDriverCell === undefined)
                && ((originStopEarly !== undefined && today === originStopEarly)
                    || (originStopLate !== undefined && today === originStopLate)
                    || (originStopEarly !== undefined && originStopLate !== undefined && today > originStopEarly && today < originStopLate)
                    || (originStopEarly !== undefined && tomorrow === originStopEarly)
                    || (originStopLate !== undefined && tomorrow === originStopLate)
                    || (originStopEarly !== undefined && originStopLate !== undefined && tomorrow > originStopEarly && tomorrow < originStopLate))
        }
    }

    static readonly FILTER_EXCLUDE_BUY_TO_MARKET = {
        id: "excludeBuyToMarket",
        caption: "Buy To Market",
        filter: (row: ModelRow) => {
            return !row.getBoolean("buy_to_market_ex", false);
        }
    }

    static readonly FILTER_ARRIVED_AT_SHIPPER = {
        id: "arrivedAtShipper",
        caption: "Arrived At Shipper",
        filter: (row: ModelRow) => {
            return (row.get("origin_actual_arrival") !== undefined)
                && (row.get("origin_actual_departure") === undefined)
                && (row.get("origin_stop_type") === "PU")
        }
    }

    static readonly FILTER_ARRIVED_AT_CONSIGNEE = {
        id: "arrivedAtConsignee",
        caption: "Arrived At Consignee",
        filter: (row: ModelRow) => {
            return (row.get("destination_actual_arrival") !== undefined)
                && (row.get("destination_actual_departure") === undefined)
                && (row.get("destination_stop_type") === "SO")
        }
    }

    static readonly FILTER_SHIPMENT_STATUS_ERROR = {
        id: "shipmentStatusError",
        caption: "Shipment Status Error",
        filter: (row: ModelRow) => row.getBoolean("has_shipment_error", false)
    }

    static readonly FILTER_INTERCOMPANY = {
        id: "intercompanyOrders",
        caption: "Intercompany Orders",
        filter: (row: ModelRow) => !StringUtil.isEmptyString(row.getString("ediorder_intercompany_key", "")),
        visible: (data: any) => {
            const row = data as ModelRow;
            return row.getBoolean("licensed_for_ic", false);
        }
    }

    static readonly FILTER_WATERFALL_IN_PROGRESS = {
        id: "waterfallInProgress",
        caption: "Waterfall In Progress",
        filter: (row: ModelRow) => row.getBoolean("waterfall_in_progress", false)
    }

    static readonly FILTER_ACTIVE_COUNTERS = {
        id: "activeCounters",
        caption: "Active Counters",
        filter: (row: ModelRow) => row.get("active_counters", 0) > 0
    }

    static readonly FILTER_FREE_TIME_STARTED = {
        id: "freeTimeStarted",
        caption: "Free Time Started",
        filter: (row: ModelRow) =>
            "F" === row.getString("origin_detention", "")
            || "F" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("F")
    }

    static readonly FILTER_DETENTION_CHARGING = {
        id: "detentionCharging",
        caption: "Detention Charging",
        filter: (row: ModelRow) =>
            "B" === row.getString("origin_detention", "")
            || "B" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("B")
    }

    static readonly FILTER_DETENTION_OCCURRED = {
        id: "detentionOccurred",
        caption: "Detention Occurred",
        filter: (row: ModelRow) =>
            "D" === row.getString("origin_detention", "")
            || "D" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("D")
    }

    static readonly FILTER_DETENTION_CLOSED = {
        id: "detentionClosed",
        caption: "Detention Closed",
        filter: (row: ModelRow) =>
            "C" === row.getString("origin_detention", "")
            || "C" === row.getString("destination_detention", "")
            || row.get("stop_detention_statuses", []).includes("C")
    }

    // buckets/tabs
    static readonly PERISCOPE = new BrokerageMovementTableType({
        revenueColumnFields: ["total_charge"],
        caption: "Periscope",
        tableIdSuffix: "All",
        filter: (row, profile) => this.holdFilter(row, profile),
        defaultSort: [{ field: "origin_early", sort: "asc" }],
        secondaryFilters: [
            BrokerageMovementTableType.FILTER_CHECK_CALL_MISS
        ]
    });

    static readonly AVAILABLE = new BrokerageMovementTableType({
        revenueColumnFields: ["pnn_rate", "calc_target_pay", "calc_max_pay"],
        caption: "Available Orders",
        tableIdSuffix: "Available",
        filter: (row, profile) => row.get("move_status", null) === "A"
            && this.holdFilter(row, profile),
        secondaryFilters: [
            BrokerageMovementTableType.FILTER_APPT_REQUIRED,
            BrokerageMovementTableType.FILTER_APPT_CONFIRMED,
            BrokerageMovementTableType.FILTER_LATE,
            BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR,
            BrokerageMovementTableType.FILTER_INTERCOMPANY,
            BrokerageMovementTableType.FILTER_SPOT_ORDERS,
            BrokerageMovementTableType.FILTER_COMMITTED_ORDERS,
            BrokerageMovementTableType.FILTER_WATERFALL_IN_PROGRESS,
            BrokerageMovementTableType.FILTER_ACTIVE_COUNTERS
        ],
        defaultSort: [{ field: "origin_early", sort: "asc" }],
        moveStatus: "A"
    });

    static readonly COVERED = new BrokerageMovementTableType({
        caption: "Covered Orders",
        tableIdSuffix: "Covered",
        filter: (row, profile) => row.get("move_status", null) === "C"
            && this.holdFilter(row, profile),
        secondaryFilters: [
            BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TODAY,
            BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TOMORROW,
            BrokerageMovementTableType.FILTER_NO_DRIVER_INFO_TOTAL,
            BrokerageMovementTableType.FILTER_TRACKING_LATE,
            BrokerageMovementTableType.TRACKING_ALERT,
            BrokerageMovementTableType.TRACKING_REQUIRED,
            BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR,
            BrokerageMovementTableType.FILTER_ARRIVED_AT_SHIPPER,
            BrokerageMovementTableType.FILTER_CHECK_CALL_MISS,
            BrokerageMovementTableType.FILTER_MISS_NEXT_CALL_DATE,
            BrokerageMovementTableType.FILTER_CHECK_CALL_APPR,
            BrokerageMovementTableType.FILTER_LATE,
            BrokerageMovementTableType.FILTER_APPT_REQUIRED,
            BrokerageMovementTableType.FILTER_APPT_CONFIRMED,
            BrokerageMovementTableType.FILTER_INTERCOMPANY,
            BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET,
            BrokerageMovementTableType.LOAD_LOCK_ALERT,
            BrokerageMovementTableType.FILTER_FREE_TIME_STARTED,
            BrokerageMovementTableType.FILTER_DETENTION_CHARGING,
            BrokerageMovementTableType.FILTER_DETENTION_CLOSED
        ],

        defaultSort: [{ field: "origin_early", sort: "asc" }],
        moveStatus: "C"
    });

    static readonly PROGRESS = new BrokerageMovementTableType({
        caption: "In Progress Orders",
        tableIdSuffix: "InProgress",
        filter: (row, profile) => row.get("move_status", null) === "P"
            && this.holdFilter(row, profile),
        secondaryFilters: [
            BrokerageMovementTableType.FILTER_TRACKING_LATE,
            BrokerageMovementTableType.TRACKING_ALERT,
            BrokerageMovementTableType.TRACKING_REQUIRED,
            BrokerageMovementTableType.FILTER_CHECK_CALL_MISS,
            BrokerageMovementTableType.FILTER_LATE,
            BrokerageMovementTableType.FILTER_SERV_FAILURE,
            BrokerageMovementTableType.FILTER_ARRIVED_AT_CONSIGNEE,
            BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR,
            BrokerageMovementTableType.FILTER_MISS_NEXT_CALL_DATE,
            BrokerageMovementTableType.FILTER_CHECK_CALL_APPR,
            BrokerageMovementTableType.FILTER_APPT_REQUIRED,
            BrokerageMovementTableType.FILTER_APPT_CONFIRMED,
            BrokerageMovementTableType.FILTER_INTERCOMPANY,
            BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET,
            BrokerageMovementTableType.FILTER_TRACKING_LATE,
            BrokerageMovementTableType.LOAD_LOCK_ALERT,
            BrokerageMovementTableType.FILTER_FREE_TIME_STARTED,
            BrokerageMovementTableType.FILTER_DETENTION_CHARGING,
            BrokerageMovementTableType.FILTER_DETENTION_OCCURRED,
            BrokerageMovementTableType.FILTER_DETENTION_CLOSED
        ],
        defaultSort: [{ field: "destination_early", sort: "asc" }],
        moveStatus: "P"
    });

    static readonly DELIVERED = new BrokerageMovementTableType({
        caption: "Delivered Orders",
        tableIdSuffix: "Delivered",
        filter: (row, profile) => row.get("move_status", null) === "D",
        secondaryFilters: [
            BrokerageMovementTableType.FILTER_SERV_FAILURE,
            BrokerageMovementTableType.FILTER_APPT_REQUIRED,
            BrokerageMovementTableType.FILTER_APPT_CONFIRMED,
            BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR,
            BrokerageMovementTableType.FILTER_INTERCOMPANY,
            BrokerageMovementTableType.FILTER_EXCLUDE_BUY_TO_MARKET,
            BrokerageMovementTableType.TRACKING_ALERT,
            BrokerageMovementTableType.FILTER_DETENTION_OCCURRED,
            BrokerageMovementTableType.FILTER_DETENTION_CLOSED
        ],
        defaultSort: [{ field: "destination_early", sort: "asc" }],
        moveStatus: "D"
    });

    static readonly HOLD = new BrokerageMovementTableType({
        caption: "On Hold Orders",
        tableIdSuffix: "OnHold",
        filter: (row, profile) => row.data["hold"] === true,
        secondaryFilters: [
            BrokerageMovementTableType.FILTER_CHECK_CALL_MISS,
            BrokerageMovementTableType.FILTER_CHECK_CALL_APPR,
            BrokerageMovementTableType.FILTER_APPT_REQUIRED,
            BrokerageMovementTableType.FILTER_APPT_CONFIRMED,
            BrokerageMovementTableType.FILTER_SHIPMENT_STATUS_ERROR,
            BrokerageMovementTableType.FILTER_INTERCOMPANY
        ],
        defaultSort: [{ field: "origin_early", sort: "asc" }]
    });

    static readonly DETENTION_FILTERS = [
        BrokerageMovementTableType.FILTER_FREE_TIME_STARTED,
        BrokerageMovementTableType.FILTER_DETENTION_CHARGING,
        BrokerageMovementTableType.FILTER_DETENTION_OCCURRED,
        BrokerageMovementTableType.FILTER_DETENTION_CLOSED
    ];

    public revenueColumnFields: string[];
    public orderColumnBadgeIds: string[];
    public statusColumnBadgeIds: string[];
    public caption: string;
    public tableIdSuffix: string;
    public filter: Filter;
    private _secondaryFilters?: SecondaryFilter[];
    public configureColumn: (column: TableColumn) => void;
    public defaultSort: OrderByInfo[];
    public moveStatus?: string;

    private constructor(props: Partial<BrokerageMovementTableTypeProps>) {
        this.revenueColumnFields = props.revenueColumnFields ?? ["total_charge", "total_pay", "margin"];
        this.orderColumnBadgeIds = props.orderColumnBadgeIds ?? ["badgeHold"];
        this.statusColumnBadgeIds = ["badgeBrTrackStatus", "labelPendingBadge"];
        this.caption = props?.caption;
        this.tableIdSuffix = props?.tableIdSuffix;
        this.filter = props.filter;
        this.secondaryFilters = props.secondaryFilters;
        this.configureColumn = props.configureColumn ?? ((column: TableColumn) => this._internalConfigureColumn(column));
        this.defaultSort = props.defaultSort;
        this.moveStatus = props.moveStatus;
    }

    private _internalConfigureColumn(column: TableColumn) {
        // get ID number at the end of the column headingCell id string

        const columnId = column.headingCell.id.toLowerCase();
        if (columnId.endsWith(Columns.ORDER.valueOf().toLowerCase())) {
            if (this.orderColumnBadgeIds) {
                column.cellDef.def.components = column.cellDef.def.components.filter(comp =>
                    comp.id == null || !(comp.id as string).startsWith("badge") || this.orderColumnBadgeIds.includes(comp.id)
                );
            }
        } else if (columnId.endsWith(Columns.STATUS.valueOf().toLowerCase())) {
            if (this.statusColumnBadgeIds) {
                column.cellDef.def.components = column.cellDef.def.components.filter(comp =>
                    comp.id == null || !(comp.id as string).startsWith("badge") || this.statusColumnBadgeIds.includes(comp.id)
                );
            }
        } else if (columnId.endsWith(Columns.REVENUE.valueOf().toLowerCase())) {
            column.cellDef.def.components = column.cellDef.def.components.filter(comp => this.revenueColumnFields.includes(comp.field))
        }
    }

    private static isLateWarning(row: ModelRow, stop: "origin" | "destination"): boolean {
        if (row.get(`${stop}_actual_arrival`) != null) return false;

        const diff = getTimeDiff(new Date(PlanningUtil.getStopTime(row, stop)));
        if (diff == null) return false;
        const dispatchControl = LMEGeneralSettings.getSingleton().getDispatchControl();
        const warning = stop === "origin" ? dispatchControl.pickup_warning : dispatchControl.delivery_warning;
        return diff < (warning * 60 * 60 * 1000)
    }

    // return false if the row should not show
    public static holdFilter(row: ModelRow, profile: RowBrokeragePlanningProfile): boolean {
        if (profile.getBoolean("include_on_hold_orders", false))
            return true;
        return !row.getBoolean("hold", false);
    }

    private static isTrackingLate(row: ModelRow): boolean {
        if (row.get(`next_stop_actual_arrival`) != null) return false;

        const stopTime: Date = new Date(PlanningUtil.getStopTime(row, "next_stop"));
        const etaToNextStopTimeZone: string = row.get("next_stop_timezone") != null ? " " + row.get("next_stop_timezone") : "";
        const etaToNextStop: Date = new Date(row.getDateTime("last_callin_eta") + etaToNextStopTimeZone);
        const diff = etaToNextStop != null ? etaToNextStop.getTime() - stopTime.getTime() : null;
        if (diff == null) return false;

        return diff > 0;
    }

    public static getTypeForStatus(status: MovementStatus): BrokerageMovementTableType {
        switch (status) {
            case MovementStatus.AVAILABLE:
                return BrokerageMovementTableType.AVAILABLE;
            case MovementStatus.COVERED:
                return BrokerageMovementTableType.COVERED;
            case MovementStatus.PROGRESS:
                return BrokerageMovementTableType.PROGRESS;
            case MovementStatus.DELIVERED:
                return BrokerageMovementTableType.DELIVERED;
            default:
                return null;
        }
    }

    private static stopApptFilter(row: ModelRow<any>, prefix: string, confirmed: boolean): boolean {
        const apptRequired = (prefix != null)
            ? row.getBoolean(`${prefix}_appt_required`, false)
            : row.getBoolean('appt_required', false);
        const apptConfirmed = (prefix != null)
            ? row.getBoolean(`${prefix}_appt_confirmed`, false)
            : row.getBoolean('confirmed', false);
        const notCleared = "D" !== row.getString("status", null);
        return (confirmed)
            ? apptRequired && apptConfirmed && notCleared
            : apptRequired && !apptConfirmed && notCleared;
    }

    private static trackingRequiredAndActiveVendorExist(row: ModelRow) {
        return row.getBoolean("tracking_vendor_active", false)
            && row.getBoolean("tracking_required", false);
    }

    get secondaryFilters(): SecondaryFilter[] {
        if (this._secondaryFilters == null) {
            return [];
        }
        const showDetention = getDetentionControlBoolean("is_active", false);
        return showDetention
            ? this._secondaryFilters
            : this._secondaryFilters.filter(f => !BrokerageMovementTableType.DETENTION_FILTERS.includes(f));
    }

    set secondaryFilters(value: SecondaryFilter[]) {
        this._secondaryFilters = value;
    }
}
